import { HeaderRefreshButton } from '../ListSelection'
/**
 * Cobros: lo que se ha vendido.
 *
 * Esto es la pantalla; la tabla vive en `OrdersTable`, que comparte con la ficha
 * de una oferta. Aquí quedan las tres cosas que son de la SECCIÓN y no de la
 * lista: pedir los cobros, esperar a que lleguen y ponerles su cabecera.
 *
 * Sólo se lee. Un cobro lo escribe el webhook de Stripe cuando el pago se
 * confirma, así que no hay nada que crear, editar ni borrar — y no lo habrá:
 * inventar o corregir una venta a mano sería contradecir a Stripe, que es quien
 * tiene el dinero.
 */
import { useSectionResource } from '../../../hooks/useSectionResource'
import { SectionRefresh } from '../../SectionRefresh'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useI18n } from '../../../lib/i18n'
import { failureDetail } from './offerShared'
import { OrdersTable } from './OrdersTable'
import { listOrders } from '../../../lib/api'

export function OrdersPanel({ projectId, token, record, onRecordChange, onOpenContact, onOpenOffer, disputedOnly = false, onConsumeDisputedOnly, canSell = true, onOpenPlans }: {
  projectId: string
  token: string
  /**
   * Si el plan da para cobrar.
   *
   * Sin ventas y sin plan que las permita, «todavía no hay ventas» describe algo
   * que no puede pasar: no es que no hayan comprado, es que no se puede cobrar
   * todavía. Ahí el hueco lo ocupa la oferta.
   */
  canSell?: boolean
  onOpenPlans?: (() => void) | undefined
  /** La venta a abrir al entrar. Viene de la campana, que enlaza a una concreta. */
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  /**
   * Entrar ya filtrado por disputadas. Lo pide el aviso de arriba cuando hay
   * varias: con una sola abre su ficha y no llega a pasar por aquí.
   */
  disputedOnly?: boolean
  /** Se consume al aplicarlo: es un empujón de una vez, no un modo pegado. */
  onConsumeDisputedOnly?: (() => void) | undefined
  /** Abre la ficha del comprador en Contactos, como hace Formularios. */
  onOpenContact?: ((identity: string) => void) | undefined
  /** Abre la oferta que se vendió, para editarla. */
  onOpenOffer?: ((offerId: string) => void) | undefined
}) {
  const { t } = useI18n()
  const result = useSectionResource(token, [projectId, 'orders'], () => listOrders(projectId, token))
  const orders = result.data ?? []
  const ordersLoader = useRecordLoader(result.loading)
  const failed = Boolean(result.error)
  const failureReason = result.error ? failureDetail(result.error) : ''

  /* La espera sustituye a la sección ENTERA: sin cabecera, sin barra y sin
     tabla, porque una pantalla a medio pintar mueve sus piezas cada vez que
     llega una. Ver `SectionLoader`. */
  if (ordersLoader.visible) return <SectionLoader loader={ordersLoader} label={t('orders.loading')} />

  return (
    <div className="dash-projects-view dash-pages-view dash-orders-list-view dash-loaded-content-in">
      <SectionRefresh refreshing={result.refreshing} failed={failed} />
      <DashboardPageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        actions={<HeaderRefreshButton refreshing={result.refreshing} onRefresh={() => { void result.refresh() }} />}
      />

      <OrdersTable
        projectId={projectId}
        token={token}
        orders={orders}
        failed={failed && result.data === undefined}
        failureReason={failureReason}
        onReload={() => { void result.refresh() }}
        canSell={canSell}
        onOpenPlans={onOpenPlans}
        record={record}
        onRecordChange={onRecordChange}
        disputedOnly={disputedOnly}
        onConsumeDisputedOnly={onConsumeDisputedOnly}
        onOpenContact={onOpenContact}
        onOpenOffer={onOpenOffer}
      />
    </div>
  )
}
